import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  MessageSquare,
  Star,
  Loader2,
  Trash2,
  ArrowUpRight,
  LayoutDashboard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const TABS = [
  { id: "resources", label: "My Resources" },
  { id: "ai", label: "AI History" },
];

export default function Dashboard() {
  const { user, loading, openAuth, refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("resources");
  const [conversations, setConversations] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [loadingAi, setLoadingAi] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      openAuth({
        tab: "login",
        context: {
          title: "Sign in to open your dashboard",
          subtitle: "Your dashboard shows your bookmarks and AI history.",
        },
      });
      return;
    }
    api.get("/roadmaps").then((r) => setHubs(r.data)).catch(() => setHubs([]));
    setLoadingAi(true);
    api
      .get("/user/conversations")
      .then((r) => setConversations(r.data.items || []))
      .catch(() => setConversations([]))
      .finally(() => setLoadingAi(false));
  }, [user, loading, openAuth]);

  if (loading || !user) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your dashboard…
      </div>
    );
  }

  const bySlugHub = Object.fromEntries(hubs.map((h) => [h.slug, h]));

  const bookmarkedHubs = (user.bookmarked_hubs || []).map((s) => bySlugHub[s]).filter(Boolean);

  const initials = (user.full_name || user.email || "U").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const deleteConversation = async (session_id) => {
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/user/conversations/${session_id}`);
      setConversations((prev) => prev.filter((c) => c.session_id !== session_id));
      toast.success("Conversation deleted.");
    } catch (_) {
      toast.error("Please try again.");
    }
  };

  const renameConversation = async (session_id, currentTitle) => {
    const next = window.prompt("Rename conversation", currentTitle || "");
    if (next == null || next.trim() === "") return;
    try {
      await api.patch(`/user/conversations/${session_id}`, { title: next.trim() });
      setConversations((prev) => prev.map((c) => (c.session_id === session_id ? { ...c, title: next.trim() } : c)));
      toast.success("Conversation renamed.");
    } catch (_) {
      toast.error("Please try again.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="flex items-center gap-4">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-[#0A2540] text-white grid place-items-center font-display text-lg">
            {initials}
          </div>
        )}
        <div>
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500 flex items-center gap-2">
            <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-[#0A2540] font-medium mt-1">
            Welcome back, {(user.full_name || user.email).split(" ")[0]}.
          </h1>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-3">
        <StatBox label="Bookmarked hubs" value={bookmarkedHubs.length} />
        <StatBox label="AI conversations" value={conversations.length} />
      </div>

      <div className="mt-8 inline-flex gap-1 rounded-full bg-slate-100 p-1 max-w-full overflow-x-auto" data-testid="dashboard-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`dashboard-tab-${t.id}`}
            className={`text-xs sm:text-sm px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
              tab === t.id ? "bg-white text-[#0A2540] shadow-sm font-semibold" : "text-slate-500 hover:text-[#0A2540]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "resources" && (
          <SectionGroup>
            <SectionTitle icon={Star} title="Bookmarked Career Hubs" count={bookmarkedHubs.length} />
            {bookmarkedHubs.length === 0 ? (
              <EmptyState
                icon={Star}
                title="No bookmarked Career Hubs."
                body="Bookmark your favourite Career Hubs from the Career Hubs page."
                cta={{ to: "/roadmaps", label: "Explore Career Hubs" }}
              />
            ) : (
              <Grid>
                {bookmarkedHubs.map((h) => (
                  <HubCard key={h.slug} h={h} />
                ))}
              </Grid>
            )}
          </SectionGroup>
        )}

        {tab === "ai" && (
          <SectionGroup>
            <SectionTitle icon={MessageSquare} title="AI conversations" count={conversations.length} />
            {loadingAi ? (
              <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : conversations.length === 0 ? (
              <EmptyState
                icon={MessageSquare}
                title="No conversations yet."
                body="Start a chat with VantStride AI — every conversation is auto-saved to your dashboard."
                cta={{ to: "/career-ai", label: "Chat with VantStride AI" }}
              />
            ) : (
              <div className="grid gap-3">
                {conversations.map((c) => (
                  <motion.div
                    key={c.session_id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl border border-slate-100 bg-white p-4 flex flex-wrap items-center gap-3"
                    data-testid={`conversation-row-${c.session_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0A2540] truncate">{c.title || "Conversation"}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        {new Date(c.last_message_at || c.created_at).toLocaleString()} · {c.message_count || 0} messages
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/career-ai?c=${c.session_id}`)}
                        data-testid={`conversation-open-${c.session_id}`}
                        className="text-xs font-medium bg-[#0A2540] text-white rounded-full px-3.5 py-2 hover:bg-[#051A2E] transition-colors inline-flex items-center gap-1.5"
                      >
                        Continue <ArrowUpRight className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => renameConversation(c.session_id, c.title)}
                        className="text-xs text-slate-600 border border-slate-200 rounded-full px-3 py-2 hover:bg-slate-50"
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteConversation(c.session_id)}
                        data-testid={`conversation-delete-${c.session_id}`}
                        className="text-xs text-slate-500 border border-slate-200 rounded-full p-2 hover:bg-slate-50"
                        aria-label="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </SectionGroup>
        )}

      </div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4">
      <div className="font-display text-3xl text-[#0A2540]">{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
    </div>
  );
}

function SectionGroup({ children }) {
  return <div className="space-y-8">{children}</div>;
}

function SectionTitle({ icon: Icon, title, count }) {
  return (
    <div className="flex items-baseline gap-2">
      <Icon className="w-4 h-4 text-[#0A2540]" />
      <h2 className="font-display text-xl text-[#0A2540] font-medium">{title}</h2>
      <span className="text-xs text-slate-500">({count})</span>
    </div>
  );
}

function EmptyState({ icon: Icon, title, body, cta }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/50 p-8 text-center">
      <div className="w-10 h-10 mx-auto rounded-2xl bg-white border border-slate-200 grid place-items-center">
        <Icon className="w-4 h-4 text-[#0A2540]" />
      </div>
      <div className="font-display text-lg text-[#0A2540] mt-3">{title}</div>
      <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">{body}</p>
      {cta && (
        <Link
          to={cta.to}
          className="mt-5 inline-flex items-center gap-2 bg-[#0A2540] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#051A2E] transition-colors"
        >
          {cta.label}
        </Link>
      )}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>;
}

function HubCard({ h }) {
  return (
    <Link
      to={`/roadmaps/${h.slug}`}
      className="rounded-2xl border border-slate-100 bg-white p-5 vestryn-card-lift block"
    >
      <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500">Career Hub</div>
      <div className="font-display text-lg text-[#0A2540] mt-1">{h.title}</div>
      <div className="text-sm text-slate-600 mt-1">{h.tagline}</div>
    </Link>
  );
}

